import { forwardRef, useState, useEffect, useMemo, useRef } from "react";
import type { Ref, ChangeEvent, ReactNode, ForwardedRef } from "react";
import type { MenuPosition } from "react-select";
import { cx } from "../../../utils";
import { InputSize, getDaysInMonth } from "./utils";
import { DayInput } from "./day-input";
import { MonthSelect } from "./month-select";
import { YearInput } from "./year-input";

interface InputProps {
  label?: string | ReactNode;
  size?: InputSize;
  id: string;
  placeholder?: string;
  type?: string;
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  className?: string;
  error?: string;
  maxLength?: number;
  max?: number;
  checked?: boolean;
  focusOnError?: boolean;
  defaultValue?: string;
  borderLess?: boolean;
  readOnly?: boolean;
  list?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  hideOptional?: boolean;
  ignoreDay?: boolean;
  locale?: string;

  // For month dropdown
  menuPosition?: MenuPosition;
}

const translateOptional = (locale: string) => {
  switch (locale) {
    case "es":
      return " (opcional)";
    case "en":
    default:
      return " (optional)";
  }
};

const translateInvalidDate = (locale: string) => {
  switch (locale) {
    case "es":
      return "Fecha inválida";
    case "en":
    default:
      return "Invalid date";
  }
};

// Prefill on this won't work.

const DateInputComponent = (
  props: InputProps,
  ref?: ForwardedRef<HTMLInputElement>
) => {
  const {
    label,
    id,
    // placeholder,
    // type,
    size = InputSize.BASE,
    onChange,
    className,
    error,
    value,
    readOnly,
    focusOnError,
    borderLess,
    ignoreDay,
    required,
    hideOptional,
    locale = "en",
    menuPosition
    // ...rest
  } = props;

  const yearRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (focusOnError && error) {
      (
        yearRef as {
          current?: {
            focus?: () => void;
          };
        }
      ).current?.focus?.();
    }
  }, [focusOnError, error]);

  useEffect(() => {
    if (typeof value === "string" && value) {
      // trim T part from ISO date
      const date = value.includes("T") ? value.split("T")[0] : value;
      const [y, m, d] = date.includes("-") ? date.split("-") : date.split("/");

      setYear(y);
      setMonth(m);
      setDay(ignoreDay ? "01" : parseInt(d).toString());
    }
  }, [value]);

  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  // Validate day - like 30 february / 31 september
  const isValidDay = useMemo(() => {
    if (ignoreDay) return true;
    // Ignore check if month or year is not set
    if (!month || !year) return true;
    return Number(day) <= getDaysInMonth(Number(year), Number(month));
  }, [day, month, year, ignoreDay]);

  const transformedDay = (dayValue: string) => {
    if (ignoreDay) return "01";
    if (!dayValue) return "";
    return dayValue.length === 2 ? dayValue : dayValue.padStart(2, "0");
  };

  const isoDate = useMemo(() => {
    if (!year || !month || (!day && !ignoreDay) || !isValidDay) {
      onChange &&
        onChange({
          // @ts-expect-error -- well, that's fine
          target: {
            id,
            name,
            value: ""
          } as unknown as EventTarget
        });
      return "";
    }

    const d = new Date(
      Number(year),
      Number(month) - 1,
      ignoreDay ? 1 : Number(day)
    );

    if (d instanceof Date && !isNaN(Number(d))) {
      const dayStr = transformedDay(day);
      onChange &&
        onChange({
          // @ts-expect-error -- well, that's fine
          target: {
            id,
            value: `${year}-${month}-${dayStr}`
          } as unknown as EventTarget
        });
      return `${year}-${month}-${dayStr}`;
    }

    onChange &&
      onChange({
        // @ts-expect-error -- well, that's fine
        target: {
          id,
          name,
          value: ""
        } as unknown as EventTarget
      });
    return "";
  }, [day, month, year, ignoreDay, isValidDay]);

  return (
    <>
      <input id={id} name={id} ref={ref} type="hidden" value={isoDate} />

      {!borderLess && label ? (
        <label
          className="text-content-700 mb-1 block text-sm font-[500]"
          htmlFor={id}
        >
          {label}
          {!required && !hideOptional ? (
            <span className="text-content-100">
              {translateOptional(locale)}
            </span>
          ) : null}
        </label>
      ) : null}

      <div className={cx("flex gap-2", className)}>
        <MonthSelect
          menuPosition={menuPosition}
          locale={locale}
          error={Boolean(error || !isValidDay)}
          onChange={setMonth}
          onFocus={() => {
            yearRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          }}
          readOnly={readOnly}
          size={size}
          value={month}
        />
        {ignoreDay ? null : (
          <DayInput
            locale={locale}
            error={Boolean(error || !isValidDay)}
            onChange={setDay}
            readOnly={readOnly}
            size={size}
            value={day}
          />
        )}
        <YearInput
          locale={locale}
          error={Boolean(error || !isValidDay)}
          onChange={setYear}
          readOnly={readOnly}
          ref={yearRef as Ref<HTMLInputElement>}
          size={size}
          value={year}
        />
      </div>

      {error || !isValidDay ? (
        <p className="text-danger-500 text-xs mt-2">
          {!isValidDay ? translateInvalidDate(locale) : error}
        </p>
      ) : null}
    </>
  );
};

const DateInput = forwardRef(DateInputComponent);
DateInput.displayName = "DateInput";

export { DateInput, InputSize };
